import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Edit, Trash2, GripVertical, ChevronUp, ChevronDown,
  ExternalLink, Search, Shield, Star, Tag, Layers, LayoutGrid,
  FileText, Save, X, Eye, EyeOff, Award
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const t = localStorage.getItem('area_auth_token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

// ═══════════════════════════════════════════
// CATEGORIES TAB
// ═══════════════════════════════════════════
function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCat, setEditCat] = useState(null);
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [editSub, setEditSub] = useState(null);
  const [showSubDialog, setShowSubDialog] = useState(false);
  const [subParentId, setSubParentId] = useState('');
  const [expandedCat, setExpandedCat] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/partners/admin/categories`, { headers: authHeaders() });
    if (res.ok) { const d = await res.json(); setCategories(d.categories || []); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveCat = async () => {
    const isNew = !editCat.category_id;
    const url = isNew ? `${API}/partners/admin/categories` : `${API}/partners/admin/categories/${editCat.category_id}`;
    const method = isNew ? 'POST' : 'PUT';
    const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(editCat) });
    if (res.ok) { toast.success(isNew ? 'Category created' : 'Category updated'); setShowCatDialog(false); load(); }
    else toast.error('Failed to save category');
  };

  const deleteCat = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    const res = await fetch(`${API}/partners/admin/categories/${id}`, { method: 'DELETE', headers: authHeaders() });
    if (res.ok) { toast.success('Category removed'); load(); }
  };

  const moveCat = async (idx, dir) => {
    const arr = [...categories];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= arr.length) return;
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    const order = arr.map((c, i) => ({ category_id: c.category_id, sort_order: i + 1 }));
    await fetch(`${API}/partners/admin/categories/reorder`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ order }) });
    load();
  };

  const saveSub = async () => {
    const isNew = !editSub.subcategory_id;
    const url = isNew ? `${API}/partners/admin/subcategories` : `${API}/partners/admin/subcategories/${editSub.subcategory_id}`;
    const method = isNew ? 'POST' : 'PUT';
    const body = isNew ? { ...editSub, category_id: subParentId } : editSub;
    const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
    if (res.ok) { toast.success(isNew ? 'Subcategory created' : 'Subcategory updated'); setShowSubDialog(false); load(); }
    else toast.error('Failed to save subcategory');
  };

  const deleteSub = async (id) => {
    if (!window.confirm('Delete this subcategory?')) return;
    await fetch(`${API}/partners/admin/subcategories/${id}`, { method: 'DELETE', headers: authHeaders() });
    toast.success('Subcategory removed'); load();
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-gray-50 rounded-lg animate-pulse" />)}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{categories.length} categories</p>
        <Button size="sm" onClick={() => { setEditCat({ name: '', slug: '', description: '', sort_order: categories.length + 1, is_active: true }); setShowCatDialog(true); }} data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Category
        </Button>
      </div>

      <div className="space-y-2">
        {categories.map((cat, idx) => (
          <Card key={cat.category_id} className="border-gray-100" data-testid={`cat-row-${cat.slug}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => moveCat(idx, -1)} disabled={idx === 0} className="text-gray-300 hover:text-gray-600 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => moveCat(idx, 1)} disabled={idx === categories.length - 1} className="text-gray-300 hover:text-gray-600 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-[#0f1f33] truncate">{cat.name}</h3>
                    <Badge className={`text-[10px] ${cat.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{cat.is_active ? 'Active' : 'Inactive'}</Badge>
                    <span className="text-xs text-gray-400">{cat.partner_count} placement{cat.partner_count !== 1 ? 's' : ''}</span>
                  </div>
                  <p className="text-xs text-gray-400 truncate">{cat.description}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setExpandedCat(expandedCat === cat.category_id ? '' : cat.category_id)} data-testid={`toggle-subs-${cat.slug}`}>
                    <Layers className="w-3.5 h-3.5 mr-1" /> {cat.subcategories?.length || 0} subs
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { setEditCat({ ...cat }); setShowCatDialog(true); }}><Edit className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500 hover:text-red-700" onClick={() => deleteCat(cat.category_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              {/* Subcategories */}
              {expandedCat === cat.category_id && (
                <div className="mt-3 pl-8 space-y-1.5 border-t pt-3">
                  {cat.subcategories?.map(sub => (
                    <div key={sub.subcategory_id} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded-md">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-600">{sub.name}</span>
                        <Badge className={`text-[9px] ${sub.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{sub.is_active ? 'Active' : 'Off'}</Badge>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => { setEditSub({ ...sub }); setShowSubDialog(true); }}><Edit className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-red-500" onClick={() => deleteSub(sub.subcategory_id)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" className="text-xs" onClick={() => { setSubParentId(cat.category_id); setEditSub({ name: '', slug: '', description: '', sort_order: (cat.subcategories?.length || 0) + 1, is_active: true }); setShowSubDialog(true); }}>
                    <Plus className="w-3 h-3 mr-1" /> Add Subcategory
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Category Dialog */}
      <Dialog open={showCatDialog} onOpenChange={setShowCatDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editCat?.category_id ? 'Edit Category' : 'New Category'}</DialogTitle></DialogHeader>
          {editCat && (
            <div className="space-y-3">
              <div><label className="text-xs font-medium text-gray-500">Name</label><Input value={editCat.name} onChange={e => setEditCat(p => ({ ...p, name: e.target.value }))} data-testid="cat-name-input" /></div>
              <div><label className="text-xs font-medium text-gray-500">Slug</label><Input value={editCat.slug} onChange={e => setEditCat(p => ({ ...p, slug: e.target.value }))} placeholder="auto-generated" /></div>
              <div><label className="text-xs font-medium text-gray-500">Description</label><Textarea value={editCat.description} onChange={e => setEditCat(p => ({ ...p, description: e.target.value }))} rows={2} /></div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editCat.is_active} onChange={e => setEditCat(p => ({ ...p, is_active: e.target.checked }))} className="rounded" />
                <label className="text-sm text-gray-600">Active</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCatDialog(false)}>Cancel</Button>
            <Button onClick={saveCat} className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white" data-testid="save-category-btn"><Save className="w-4 h-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subcategory Dialog */}
      <Dialog open={showSubDialog} onOpenChange={setShowSubDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editSub?.subcategory_id ? 'Edit Subcategory' : 'New Subcategory'}</DialogTitle></DialogHeader>
          {editSub && (
            <div className="space-y-3">
              <div><label className="text-xs font-medium text-gray-500">Name</label><Input value={editSub.name} onChange={e => setEditSub(p => ({ ...p, name: e.target.value }))} data-testid="sub-name-input" /></div>
              <div><label className="text-xs font-medium text-gray-500">Slug</label><Input value={editSub.slug} onChange={e => setEditSub(p => ({ ...p, slug: e.target.value }))} placeholder="auto-generated" /></div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editSub.is_active} onChange={e => setEditSub(p => ({ ...p, is_active: e.target.checked }))} className="rounded" />
                <label className="text-sm text-gray-600">Active</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSubDialog(false)}>Cancel</Button>
            <Button onClick={saveSub} className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white" data-testid="save-subcategory-btn"><Save className="w-4 h-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════
// PARTNERS TAB
// ═══════════════════════════════════════════
function PartnersTab() {
  const [partners, setPartners] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editPartner, setEditPartner] = useState(null);
  const [categories, setCategories] = useState([]);
  const [badges, setBadges] = useState([]);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    const [pRes, cRes, bRes] = await Promise.all([
      fetch(`${API}/partners/admin/partners?page=${page}&limit=30`, { headers: authHeaders() }),
      fetch(`${API}/partners/admin/categories`, { headers: authHeaders() }),
      fetch(`${API}/partners/admin/badges`, { headers: authHeaders() }),
    ]);
    if (pRes.ok) { const d = await pRes.json(); setPartners(d.partners || []); setTotal(d.total || 0); }
    if (cRes.ok) { const d = await cRes.json(); setCategories(d.categories || []); }
    if (bRes.ok) { const d = await bRes.json(); setBadges(d.badges || []); }
    setLoading(false);
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const blankPartner = () => ({
    name: '', slug: '', short_description: '', full_description: '',
    logo_url: '', website_url: '', affiliate_url: '',
    primary_cta_text: 'Visit Partner', coupon_code: '', discount_text: '',
    is_featured: false, is_sponsored: false, is_verified: false,
    is_area_member_benefit: false, is_exclusive_discount: false, is_recommended: false,
    display_priority: 0, serves_national: true, serves_local: false, is_active: true,
    category_assignments: [], badge_ids: [],
  });

  const savePartner = async () => {
    const isNew = !editPartner.partner_id;
    const url = isNew ? `${API}/partners/admin/partners` : `${API}/partners/admin/partners/${editPartner.partner_id}`;
    const method = isNew ? 'POST' : 'PUT';
    const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(editPartner) });
    if (res.ok) { toast.success(isNew ? 'Partner created' : 'Partner updated'); setShowDialog(false); load(); }
    else toast.error('Failed to save partner');
  };

  const deletePartner = async (id) => {
    if (!window.confirm('Delete this partner and all placements?')) return;
    const res = await fetch(`${API}/partners/admin/partners/${id}`, { method: 'DELETE', headers: authHeaders() });
    if (res.ok) { toast.success('Partner deleted'); load(); }
  };

  const toggleCatAssign = (catId) => {
    if (!editPartner) return;
    const exists = editPartner.category_assignments.find(a => a.category_id === catId);
    if (exists) setEditPartner(p => ({ ...p, category_assignments: p.category_assignments.filter(a => a.category_id !== catId) }));
    else setEditPartner(p => ({ ...p, category_assignments: [...p.category_assignments, { category_id: catId }] }));
  };

  const toggleBadge = (badgeId) => {
    if (!editPartner) return;
    const exists = editPartner.badge_ids?.includes(badgeId);
    if (exists) setEditPartner(p => ({ ...p, badge_ids: p.badge_ids.filter(b => b !== badgeId) }));
    else setEditPartner(p => ({ ...p, badge_ids: [...(p.badge_ids || []), badgeId] }));
  };

  const filtered = search ? partners.filter(p => p.name.toLowerCase().includes(search.toLowerCase())) : partners;

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-gray-50 rounded-lg animate-pulse" />)}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter partners..." className="pl-10 h-9" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{total} total</span>
          <Button size="sm" onClick={() => { setEditPartner(blankPartner()); setShowDialog(true); }} data-testid="add-partner-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Partner
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {filtered.map(p => (
          <Card key={p.partner_id} className="border-gray-100" data-testid={`partner-row-${p.slug}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-gray-50 border flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {p.logo_url ? <img src={p.logo_url} alt="" className="w-8 h-8 object-contain" /> : <Tag className="w-4 h-4 text-gray-300" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-[#0f1f33]">{p.name}</h3>
                    {!p.is_active && <Badge className="text-[9px] bg-red-50 text-red-600">Inactive</Badge>}
                    {p.is_featured && <Badge className="text-[9px] bg-amber-50 text-amber-700">Featured</Badge>}
                    {p.is_sponsored && <Badge className="text-[9px] bg-violet-50 text-violet-700">Sponsored</Badge>}
                    {p.is_verified && <Badge className="text-[9px] bg-blue-50 text-blue-700">Verified</Badge>}
                  </div>
                  <p className="text-xs text-gray-400 truncate">{p.short_description}</p>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {p.categories?.map((c, i) => <Badge key={i} className="text-[9px] bg-gray-100 text-gray-500">{c.name}</Badge>)}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-xs text-gray-400">{p.click_count || 0} clicks</span>
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => {
                    setEditPartner({
                      ...p,
                      category_assignments: p.categories?.map(c => ({ category_id: c.category_id })) || [],
                      badge_ids: [],
                    });
                    setShowDialog(true);
                  }}><Edit className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500 hover:text-red-700" onClick={() => deletePartner(p.partner_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {total > 30 && (
        <div className="flex justify-center gap-2 pt-2">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
          <span className="text-sm text-gray-500 self-center">Page {page}</span>
          <Button variant="ghost" size="sm" disabled={page * 30 >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* Partner Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editPartner?.partner_id ? 'Edit Partner' : 'New Partner'}</DialogTitle></DialogHeader>
          {editPartner && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-gray-500">Name *</label><Input value={editPartner.name} onChange={e => setEditPartner(p => ({ ...p, name: e.target.value }))} data-testid="partner-name-input" /></div>
                <div><label className="text-xs font-medium text-gray-500">Slug</label><Input value={editPartner.slug} onChange={e => setEditPartner(p => ({ ...p, slug: e.target.value }))} placeholder="auto-generated" /></div>
              </div>
              <div><label className="text-xs font-medium text-gray-500">Short Description</label><Input value={editPartner.short_description} onChange={e => setEditPartner(p => ({ ...p, short_description: e.target.value }))} /></div>
              <div><label className="text-xs font-medium text-gray-500">Full Description</label><Textarea value={editPartner.full_description} onChange={e => setEditPartner(p => ({ ...p, full_description: e.target.value }))} rows={3} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-gray-500">Logo URL</label><Input value={editPartner.logo_url} onChange={e => setEditPartner(p => ({ ...p, logo_url: e.target.value }))} /></div>
                <div><label className="text-xs font-medium text-gray-500">Website URL</label><Input value={editPartner.website_url} onChange={e => setEditPartner(p => ({ ...p, website_url: e.target.value }))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-gray-500">Affiliate URL</label><Input value={editPartner.affiliate_url} onChange={e => setEditPartner(p => ({ ...p, affiliate_url: e.target.value }))} /></div>
                <div><label className="text-xs font-medium text-gray-500">CTA Text</label><Input value={editPartner.primary_cta_text} onChange={e => setEditPartner(p => ({ ...p, primary_cta_text: e.target.value }))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-gray-500">Coupon Code</label><Input value={editPartner.coupon_code || ''} onChange={e => setEditPartner(p => ({ ...p, coupon_code: e.target.value }))} /></div>
                <div><label className="text-xs font-medium text-gray-500">Discount Text</label><Input value={editPartner.discount_text || ''} onChange={e => setEditPartner(p => ({ ...p, discount_text: e.target.value }))} /></div>
              </div>
              <div><label className="text-xs font-medium text-gray-500">Display Priority</label><Input type="number" value={editPartner.display_priority} onChange={e => setEditPartner(p => ({ ...p, display_priority: parseInt(e.target.value) || 0 }))} /></div>

              {/* Flags */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-2 block">Flags</label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { key: 'is_active', label: 'Active' },
                    { key: 'is_featured', label: 'Featured' },
                    { key: 'is_sponsored', label: 'Sponsored' },
                    { key: 'is_verified', label: 'Verified' },
                    { key: 'is_area_member_benefit', label: 'Member Benefit' },
                    { key: 'is_exclusive_discount', label: 'Exclusive Discount' },
                    { key: 'is_recommended', label: 'Recommended' },
                    { key: 'serves_national', label: 'Nationwide' },
                    { key: 'serves_local', label: 'Local' },
                  ].map(f => (
                    <label key={f.key} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={!!editPartner[f.key]} onChange={e => setEditPartner(p => ({ ...p, [f.key]: e.target.checked }))} className="rounded border-gray-300 w-3.5 h-3.5" />
                      <span className="text-xs text-gray-600">{f.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Category Assignments */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-2 block">Categories</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(c => {
                    const sel = editPartner.category_assignments?.some(a => a.category_id === c.category_id);
                    return (
                      <Badge key={c.category_id} className={`cursor-pointer px-2.5 py-1 text-xs ${sel ? 'bg-[#1e3a5f] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} onClick={() => toggleCatAssign(c.category_id)}>
                        {c.name}
                      </Badge>
                    );
                  })}
                </div>
              </div>

              {/* Badge Assignments */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-2 block">Badges</label>
                <div className="flex flex-wrap gap-2">
                  {badges.map(b => {
                    const sel = editPartner.badge_ids?.includes(b.badge_id);
                    return (
                      <Badge key={b.badge_id} className={`cursor-pointer px-2.5 py-1 text-xs ${sel ? 'bg-[#1e3a5f] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} onClick={() => toggleBadge(b.badge_id)}>
                        {b.label}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={savePartner} className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white" data-testid="save-partner-btn"><Save className="w-4 h-4 mr-1" /> Save Partner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════
// AD PLACEMENTS TAB
// ═══════════════════════════════════════════
function PlacementsTab() {
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState('');
  const [placements, setPlacements] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editPlacement, setEditPlacement] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const loadCats = useCallback(async () => {
    const res = await fetch(`${API}/partners/admin/categories`, { headers: authHeaders() });
    if (res.ok) { const d = await res.json(); setCategories(d.categories || []); }
  }, []);

  const loadPartners = useCallback(async () => {
    const res = await fetch(`${API}/partners/admin/partners?limit=200`, { headers: authHeaders() });
    if (res.ok) { const d = await res.json(); setPartners(d.partners || []); }
  }, []);

  const loadPlacements = useCallback(async () => {
    setLoading(true);
    const catParam = selectedCat ? `?category_id=${selectedCat}` : '';
    const res = await fetch(`${API}/partners/admin/placements${catParam}`, { headers: authHeaders() });
    if (res.ok) { const d = await res.json(); setPlacements(d.placements || []); }
    setLoading(false);
  }, [selectedCat]);

  useEffect(() => { loadCats(); loadPartners(); }, [loadCats, loadPartners]);
  useEffect(() => { loadPlacements(); }, [loadPlacements]);

  const blankPlacement = () => ({
    partner_id: '', category_id: selectedCat || '', subcategory_id: '',
    placement_size: 'small', grid_col_span: 1, grid_row_span: 1,
  });

  const savePlacement = async () => {
    const isNew = !editPlacement.placement_id;
    const url = isNew ? `${API}/partners/admin/placements` : `${API}/partners/admin/placements/${editPlacement.placement_id}`;
    const method = isNew ? 'POST' : 'PUT';
    const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(editPlacement) });
    if (res.ok) { toast.success(isNew ? 'Placement created' : 'Placement updated'); setShowDialog(false); loadPlacements(); }
    else toast.error('Failed to save placement');
  };

  const deletePlacement = async (id) => {
    if (!window.confirm('Remove this placement?')) return;
    await fetch(`${API}/partners/admin/placements/${id}`, { method: 'DELETE', headers: authHeaders() });
    toast.success('Placement removed'); loadPlacements();
  };

  // Drag-and-drop handlers
  const handleDragStart = (idx) => { setDragIdx(idx); };
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDragEnd = async () => {
    if (dragIdx !== null && dragOverIdx !== null && dragIdx !== dragOverIdx) {
      const arr = [...placements];
      const [moved] = arr.splice(dragIdx, 1);
      arr.splice(dragOverIdx, 0, moved);
      setPlacements(arr);
      const order = arr.map((p, i) => ({ placement_id: p.placement_id, placement_order: i + 1 }));
      await fetch(`${API}/partners/admin/placements/reorder`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ order }) });
      toast.success('Order updated');
    }
    setDragIdx(null);
    setDragOverIdx(null);
  };

  // Grid drag-and-drop
  const handleGridDragStart = (e, idx) => { e.dataTransfer.effectAllowed = 'move'; setDragIdx(idx); };
  const handleGridDragOver = (e, idx) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverIdx(idx); };
  const handleGridDrop = async (e, idx) => {
    e.preventDefault();
    if (dragIdx !== null && dragIdx !== idx) {
      const arr = [...placements];
      const [moved] = arr.splice(dragIdx, 1);
      arr.splice(idx, 0, moved);
      setPlacements(arr);
      const order = arr.map((p, i) => ({ placement_id: p.placement_id, placement_order: i + 1 }));
      await fetch(`${API}/partners/admin/placements/reorder`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ order }) });
      toast.success('Grid order updated');
    }
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const sizeConfig = { small: { cols: 1, rows: 1 }, medium: { cols: 2, rows: 1 }, large: { cols: 2, rows: 2 } };

  const handleSizeChange = (size) => {
    const cfg = sizeConfig[size] || sizeConfig.small;
    setEditPlacement(p => ({ ...p, placement_size: size, grid_col_span: cfg.cols, grid_row_span: cfg.rows }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-500">Category:</label>
          <select value={selectedCat} onChange={e => setSelectedCat(e.target.value)} className="h-9 rounded-md border px-2 text-sm" data-testid="placement-cat-filter">
            <option value="">All Categories</option>
            {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
          </select>
        </div>
        <Button size="sm" onClick={() => { setEditPlacement(blankPlacement()); setShowDialog(true); }} data-testid="add-placement-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Placement
        </Button>
      </div>

      {/* Visual Drag-and-Drop Grid Preview */}
      {!loading && placements.length > 0 && (
        <Card className="border-gray-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <LayoutGrid className="w-4 h-4" /> Grid Preview
              <span className="text-[10px] font-normal text-gray-400 ml-1">Drag cards to reorder</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2 auto-rows-[70px]">
              {placements.map((pl, idx) => {
                const colCls = pl.grid_col_span > 1 ? 'col-span-2' : '';
                const rowCls = pl.grid_row_span > 1 ? 'row-span-2' : '';
                const isDragging = dragIdx === idx;
                const isOver = dragOverIdx === idx && dragIdx !== idx;
                return (
                  <div
                    key={pl.placement_id}
                    draggable
                    onDragStart={(e) => handleGridDragStart(e, idx)}
                    onDragOver={(e) => handleGridDragOver(e, idx)}
                    onDrop={(e) => handleGridDrop(e, idx)}
                    onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                    className={`rounded-lg flex flex-col items-center justify-center text-xs px-2 text-center cursor-grab active:cursor-grabbing select-none transition-all
                      ${colCls} ${rowCls}
                      ${isDragging ? 'opacity-40 scale-95 border-2 border-dashed border-blue-400 bg-blue-50' : isOver ? 'border-2 border-blue-500 bg-blue-50 shadow-md' : 'bg-gray-50 border border-gray-200 hover:border-gray-300 hover:shadow-sm'}`}
                    data-testid={`grid-cell-${idx}`}
                  >
                    <GripVertical className="w-3 h-3 text-gray-300 mb-0.5" />
                    <span className="text-gray-600 font-medium truncate max-w-full">{pl.partner?.name || pl.partner_id}</span>
                    <Badge className={`mt-0.5 text-[9px] ${pl.placement_size === 'large' ? 'bg-amber-50 text-amber-700' : pl.placement_size === 'medium' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{pl.placement_size} ({pl.grid_col_span}x{pl.grid_row_span})</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Draggable Placement List */}
      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-14 bg-gray-50 rounded-lg animate-pulse" />)}</div>
      ) : placements.length === 0 ? (
        <div className="text-center py-10 text-sm text-gray-400">{selectedCat ? 'No placements in this category.' : 'Select a category or add a placement.'}</div>
      ) : (
        <div className="space-y-1.5">
          {placements.map((pl, idx) => {
            const isDragging = dragIdx === idx;
            const isOver = dragOverIdx === idx && dragIdx !== idx;
            return (
              <div
                key={pl.placement_id}
                draggable
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-3 p-3 rounded-lg cursor-grab active:cursor-grabbing select-none transition-all
                  ${isDragging ? 'opacity-40 border-2 border-dashed border-blue-400 bg-blue-50' : isOver ? 'border-2 border-blue-500 bg-blue-50 shadow-md' : 'bg-white border border-gray-100 hover:border-gray-200'}`}
                data-testid={`placement-row-${pl.placement_id}`}
              >
                <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[#0f1f33]">{pl.partner?.name || 'Unknown'}</span>
                    <Badge className={`text-[9px] ${pl.placement_size === 'large' ? 'bg-amber-50 text-amber-700' : pl.placement_size === 'medium' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{pl.placement_size}</Badge>
                    <span className="text-[10px] text-gray-400">{pl.grid_col_span}x{pl.grid_row_span}</span>
                    {!pl.is_active && <Badge className="text-[9px] bg-red-50 text-red-600">Inactive</Badge>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { setEditPlacement({ ...pl }); setShowDialog(true); }}><Edit className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500" onClick={() => deletePlacement(pl.placement_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Placement Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editPlacement?.placement_id ? 'Edit Placement' : 'New Placement'}</DialogTitle></DialogHeader>
          {editPlacement && (
            <div className="space-y-4">
              {!editPlacement.placement_id && (
                <>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Partner *</label>
                    <select value={editPlacement.partner_id} onChange={e => setEditPlacement(p => ({ ...p, partner_id: e.target.value }))} className="w-full h-9 rounded-md border px-2 text-sm" data-testid="placement-partner-select">
                      <option value="">Select partner...</option>
                      {partners.map(p => <option key={p.partner_id} value={p.partner_id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Category *</label>
                    <select value={editPlacement.category_id} onChange={e => setEditPlacement(p => ({ ...p, category_id: e.target.value }))} className="w-full h-9 rounded-md border px-2 text-sm" data-testid="placement-category-select">
                      <option value="">Select category...</option>
                      {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                    </select>
                  </div>
                </>
              )}
              <div>
                <label className="text-xs font-medium text-gray-500">Size</label>
                <div className="flex gap-2 mt-1">
                  {['small', 'medium', 'large'].map(s => (
                    <Button key={s} type="button" variant={editPlacement.placement_size === s ? 'default' : 'outline'} size="sm" onClick={() => handleSizeChange(s)} className={editPlacement.placement_size === s ? 'bg-[#1e3a5f] text-white' : ''} data-testid={`size-${s}-btn`}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-gray-500">Col Span</label><Input type="number" min="1" max="3" value={editPlacement.grid_col_span} onChange={e => setEditPlacement(p => ({ ...p, grid_col_span: parseInt(e.target.value) || 1 }))} /></div>
                <div><label className="text-xs font-medium text-gray-500">Row Span</label><Input type="number" min="1" max="3" value={editPlacement.grid_row_span} onChange={e => setEditPlacement(p => ({ ...p, grid_row_span: parseInt(e.target.value) || 1 }))} /></div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editPlacement.is_active !== false} onChange={e => setEditPlacement(p => ({ ...p, is_active: e.target.checked }))} className="rounded" />
                <label className="text-sm text-gray-600">Active</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={savePlacement} className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white" data-testid="save-placement-btn"><Save className="w-4 h-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════
// BADGES TAB
// ═══════════════════════════════════════════
function BadgesTab() {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [newBadge, setNewBadge] = useState({ name: '', badge_type: 'custom', label: '', color_theme: 'blue', icon_name: '' });

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/partners/admin/badges`, { headers: authHeaders() });
    if (res.ok) { const d = await res.json(); setBadges(d.badges || []); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveBadge = async () => {
    const res = await fetch(`${API}/partners/admin/badges`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ ...newBadge, label: newBadge.label || newBadge.name }) });
    if (res.ok) { toast.success('Badge created'); setShowDialog(false); setNewBadge({ name: '', badge_type: 'custom', label: '', color_theme: 'blue', icon_name: '' }); load(); }
    else toast.error('Failed to create badge');
  };

  const colorThemes = ['blue', 'amber', 'violet', 'emerald', 'rose', 'sky', 'gray'];
  const badgeStyleMap = { blue: 'bg-blue-50 text-blue-700', amber: 'bg-amber-50 text-amber-700', violet: 'bg-violet-50 text-violet-700', emerald: 'bg-emerald-50 text-emerald-700', rose: 'bg-rose-50 text-rose-700', sky: 'bg-sky-50 text-sky-700', gray: 'bg-gray-100 text-gray-600' };

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-12 bg-gray-50 rounded-lg animate-pulse" />)}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{badges.length} badges</p>
        <Button size="sm" onClick={() => setShowDialog(true)} data-testid="add-badge-btn"><Plus className="w-4 h-4 mr-1" /> Add Badge</Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {badges.map(b => (
          <Card key={b.badge_id} className="border-gray-100">
            <CardContent className="p-4 flex items-center gap-3">
              <Badge className={`${badgeStyleMap[b.color_theme] || badgeStyleMap.gray} text-xs px-3 py-1`}>{b.label}</Badge>
              <div>
                <p className="text-xs text-gray-400">{b.badge_type}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Badge</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs font-medium text-gray-500">Name</label><Input value={newBadge.name} onChange={e => setNewBadge(p => ({ ...p, name: e.target.value }))} data-testid="badge-name-input" /></div>
            <div><label className="text-xs font-medium text-gray-500">Label (display text)</label><Input value={newBadge.label} onChange={e => setNewBadge(p => ({ ...p, label: e.target.value }))} placeholder="Same as name" /></div>
            <div>
              <label className="text-xs font-medium text-gray-500">Type</label>
              <select value={newBadge.badge_type} onChange={e => setNewBadge(p => ({ ...p, badge_type: e.target.value }))} className="w-full h-9 rounded-md border px-2 text-sm">
                <option value="trust">Trust</option>
                <option value="promotion">Promotion</option>
                <option value="benefit">Benefit</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Color Theme</label>
              <div className="flex gap-2">
                {colorThemes.map(c => (
                  <button key={c} onClick={() => setNewBadge(p => ({ ...p, color_theme: c }))} className={`w-8 h-8 rounded-full border-2 ${newBadge.color_theme === c ? 'border-[#1e3a5f]' : 'border-transparent'}`}>
                    <Badge className={`${badgeStyleMap[c]} w-full text-[9px]`}>{c}</Badge>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={saveBadge} className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white" data-testid="save-badge-btn"><Save className="w-4 h-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════
// DISCLOSURE TAB
// ═══════════════════════════════════════════
function DisclosureTab() {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/partners/disclosure`).then(r => r.json()).then(d => { setText(d.text || ''); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    const res = await fetch(`${API}/partners/admin/disclosure`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ text }) });
    if (res.ok) toast.success('Disclosure updated');
    else toast.error('Failed to update');
  };

  if (loading) return <div className="h-32 bg-gray-50 rounded-lg animate-pulse" />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">This text appears at the bottom of every public partner page to disclose affiliate relationships.</p>
      <Textarea value={text} onChange={e => setText(e.target.value)} rows={4} data-testid="disclosure-textarea" />
      <Button onClick={save} className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white" data-testid="save-disclosure-btn"><Save className="w-4 h-4 mr-1" /> Save Disclosure</Button>
    </div>
  );
}

// ═══════════════════════════════════════════
// MAIN ADMIN PARTNERS PAGE
// ═══════════════════════════════════════════
export default function AdminPartners() {
  const { user, loading: authLoading, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) navigate('/login');
  }, [user, authLoading, isAdmin, navigate]);

  if (authLoading) return <div className="max-w-6xl mx-auto px-4 py-12"><div className="h-48 bg-gray-50 rounded-xl animate-pulse" /></div>;
  if (!user || !isAdmin) return null;

  return (
    <div className="min-h-screen bg-[#fafbfc]" data-testid="admin-partners-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Admin</Button></Link>
          <div>
            <h1 className="text-xl font-bold text-[#0f1f33]">Partner Directory Management</h1>
            <p className="text-sm text-gray-500">Manage categories, partners, ad placements, badges, and disclosure settings.</p>
          </div>
        </div>

        <Tabs defaultValue="categories">
          <TabsList className="mb-6">
            <TabsTrigger value="categories" data-testid="tab-categories"><Layers className="w-4 h-4 mr-1" /> Categories</TabsTrigger>
            <TabsTrigger value="partners" data-testid="tab-partners"><Tag className="w-4 h-4 mr-1" /> Partners</TabsTrigger>
            <TabsTrigger value="placements" data-testid="tab-placements"><LayoutGrid className="w-4 h-4 mr-1" /> Ad Placements</TabsTrigger>
            <TabsTrigger value="badges" data-testid="tab-badges"><Award className="w-4 h-4 mr-1" /> Badges</TabsTrigger>
            <TabsTrigger value="disclosure" data-testid="tab-disclosure"><FileText className="w-4 h-4 mr-1" /> Disclosure</TabsTrigger>
          </TabsList>

          <TabsContent value="categories"><CategoriesTab /></TabsContent>
          <TabsContent value="partners"><PartnersTab /></TabsContent>
          <TabsContent value="placements"><PlacementsTab /></TabsContent>
          <TabsContent value="badges"><BadgesTab /></TabsContent>
          <TabsContent value="disclosure"><DisclosureTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
